// Delivery queue list — sorted by scheduled_for ascending (server-side).
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "./logisticsRoutesShared";
import {
  ORIGIN_STOP_KIND_FILTER_LABELS,
  ROUTE_STATE_CHIP_CLASSES,
  ROUTE_STATE_LABELS,
  carrierLabel,
  formatRouteScheduled,
  formatTimestamp,
  unitLabel,
} from "./logisticsRoutesShared";
import type { useLogisticsRoutesPanel } from "./useLogisticsRoutesPanel";
import type { LogiCarrier, LogiRoute } from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturingClient";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  m: ReturnType<typeof useLogisticsRoutesPanel>;
  selectedRouteId?: string | null;
  onSelectRoute?: (routeId: string) => void;
};

function RouteRow({
  route,
  units,
  carriers,
  isSelected,
  onSelect,
}: {
  route: LogiRoute;
  units: MfgUnit[];
  carriers: LogiCarrier[];
  isSelected: boolean;
  onSelect?: () => void;
}) {
  const created = formatTimestamp(route.created_at);
  const scheduled = formatRouteScheduled(route);
  const chipClass =
    ROUTE_STATE_CHIP_CLASSES[route.route_state] ?? "bg-gray-500 text-white";
  const origin =
    route.origin_stop_kind &&
    ORIGIN_STOP_KIND_FILTER_LABELS[route.origin_stop_kind]
      ? ORIGIN_STOP_KIND_FILTER_LABELS[route.origin_stop_kind]
      : null;
  return (
    <li
      className={`flex flex-wrap items-start gap-2 py-3 ${
        isSelected ? "bg-indigo-50 dark:bg-indigo-950/30 -mx-2 px-2 rounded" : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
          <code>{unitLabel(units, route.unit_id)}</code>
          <span
            className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chipClass}`}
          >
            {ROUTE_STATE_LABELS[route.route_state] ?? route.route_state}
          </span>
        </div>
        <div className="text-xs text-gray-600 dark:text-zinc-400">
          Carrier: {carrierLabel(carriers, route.carrier_id)}
        </div>
        {origin ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Origin: {origin}</div>
        ) : null}
        {scheduled ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Scheduled: {scheduled}</div>
        ) : null}
        {route.notes ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">{route.notes}</div>
        ) : null}
        {created ? (
          <div className="text-xs text-gray-500 dark:text-zinc-500">Created {created}</div>
        ) : null}
      </div>
      {onSelect ? (
        <button
          type="button"
          className={ghostBtnClass}
          onClick={onSelect}
          aria-pressed={isSelected}
        >
          {isSelected ? "Close" : "Open"}
        </button>
      ) : null}
    </li>
  );
}

export function LogisticsRoutesList({ m, selectedRouteId, onSelectRoute }: Props) {
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "logi-routes-list" }}>
      <SectionCard title="Delivery queue">
      {m.listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading routes…</div>
      ) : null}
      {!m.listBusy && m.routes.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No routes match these filters. Use + New route to schedule a delivery.
        </div>
      ) : null}
      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {m.routes.map((r) => (
          <RouteRow
            key={r.id}
            route={r}
            units={m.units}
            carriers={m.carriers}
            isSelected={r.id === selectedRouteId}
            onSelect={onSelectRoute ? () => onSelectRoute(r.id) : undefined}
          />
        ))}
      </ul>
      </SectionCard>
    </div>
  );
}
